import { useRecoilValue } from "recoil";
import { useControllerContext } from "../../hooks/controller-context";
import * as S from "./styles";
import { pubState } from "../../../../recoil/pub.atom";
import {
  getTournamentStatusPreset,
  registerTournamentStatusPreset,
  removeTournamentStatusPreset,
  StatusPresetResponseDto,
  updateTournamentStatusPreset,
} from "../../../../api/tournament-preset";
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { formatDateTime } from "../../../../utils/date";
import { Const } from "../../../../common/const/const";
import InputDialog from "../../../../component/InputDialog";
import { Overlay } from "../../../../styles/popup";
import { exchangeSeed } from "../../../../utils/number";

const defaultPreset: StatusPresetResponseDto = {
  id: -1,
  pubId: -1,
  presetName: "기본 프리셋",
  preset: `[메인타이틀]
[서브타이틀]

⏰ 게임 시작 : [시작시간]🕖
🎫 ENTRY CONDITION : [바이인금액]

현재 LV [현재레벨]    [스몰블라인드] / [빅블라인드] / [엔티]
⏰DURATION : [듀레이션]

🔹Live Players : [현재플레이어]
🔹Total Entry : [전체엔트리]

🔅 Starting : [바이인칩]
🔅 Re-entry : [리엔트리칩]

💠 레지마감 ➳ [레지마감레벨] LV ([레지마감시간])

🎫 RABBIT은 기명, 무가치이며 거래는 어떤 경우에도 불가능합니다.
RABBIT을 양도,양수할 경우 RB 몰수 및 러너펍 모든 서비스 이용 불가합니다.`,
};

const TimerControllerStatus = () => {
  const { data, timeInfo } = useControllerContext();
  const pub = useRecoilValue(pubState);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedPreset, setSelectedPreset] =
    useState<StatusPresetResponseDto>(defaultPreset);
  const [list, setList] = useState<StatusPresetResponseDto[]>([]);
  const [presetName, setPresetName] = useState<string>("");
  const [presetContent, setPresetContent] = useState<string>("");
  const [isDropBoxOpen, setIsDropBoxOpen] = useState<boolean>(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleVariableClick = useCallback(
    (variableName: string) => {
      const textarea = textareaRef.current;
      if (!textarea) {
        setPresetContent((prev) => prev + variableName);
        return;
      }

      // 현재 커서 위치에 변수 삽입
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const before = presetContent.substring(0, start);
      const after = presetContent.substring(end);

      const newContent = before + variableName + after;
      setPresetContent(newContent);

      textarea.focus();
      setTimeout(() => {
        // 커서 위치를 변수 뒤로 이동
        const newCursorPos = start + variableName.length;
        textarea.setSelectionRange(newCursorPos, newCursorPos);
      }, 0);
    },
    [presetContent],
  );

  const getData = useCallback(async () => {
    if (!pub) return;
    const res = await getTournamentStatusPreset(pub.id);

    setList([defaultPreset, ...res.preset]);
    setSelectedPreset(defaultPreset);
  }, [pub]);

  useEffect(() => {
    setPresetContent(selectedPreset.preset);
    setPresetName(selectedPreset.presetName);
  }, [selectedPreset]);

  useEffect(() => {
    if (!data?.id) return;
    if (warning) return;
    const saved = localStorage.getItem(`status-preset-${data.id}`);
    if (saved) {
      const { presetContent, presetName } = JSON.parse(saved);
      setPresetContent(presetContent);
      setPresetName(presetName);
    }
  }, [data?.id]);

  useEffect(() => {
    if (!data?.id) return;
    localStorage.setItem(
      `status-preset-${data.id}`,
      JSON.stringify({ presetContent, presetName }),
    );
  }, [data?.id, presetContent]);

  useEffect(() => {
    getData();
  }, [getData]);

  // 레지마감 시간 계산
  const registerDeadlineTime = useCallback(() => {
    if (!data?.startDate || !data?.registerDeadlineLevel || !data?.structure)
      return "";

    const startTime = new Date(data.startDate);
    const levelIndex = data.structure.findIndex(
      (item) => item.level === data.registerDeadlineLevel,
    );

    if (levelIndex === -1) return "";

    const isNextBreak = data.structure[levelIndex + 1]?.type === "BREAK";

    const totalMinutes = data.structure
      .slice(0, levelIndex + (isNextBreak ? 2 : 1))
      .reduce((acc, item) => acc + item.time, 0);

    const deadlineDate = new Date(startTime.getTime() + totalMinutes * 60000);
    return formatDateTime(deadlineDate.toISOString(), true);
  }, [data]);

  // 듀레이션 계산
  const getDurations = useCallback(() => {
    if (!data || !data.structure) return "";

    const list: number[] = [];
    data.structure
      .filter((s) => s.type === "LEVEL")
      .forEach((s) => {
        if (list.length === 0 || list[list.length - 1] !== s.time) {
          list.push(s.time);
        }
      });

    return `${list.join("M / ")}M`;
  }, [data]);

  // 숫자 포맷
  const formatNumber = useCallback((value?: number) => {
    if (!value) return "0";
    return value.toLocaleString();
  }, []);

  // 현재 레벨
  const getCurrentLevel = useCallback(() => {
    if (timeInfo?.elapsedTimeMs && timeInfo?.elapsedTimeMs < 0)
      return "시작 준비중";
    if (timeInfo.currentLevel?.level) return timeInfo.currentLevel.level;
    if (timeInfo.currentLevel?.type === "BREAK") return "(BREAK)";
    return "종료된 게임";
  }, [timeInfo]);

  // 프리셋 사용가능 변수 리스트
  const presetVariables = useMemo(() => {
    if (!data) return [];
    return [
      { label: "메인타이틀", name: "[메인타이틀]", value: data.title },
      { label: "서브타이틀", name: "[서브타이틀]", value: data.subTitle },
      {
        label: "시작시간",
        name: "[시작시간]",
        value: formatDateTime(data.startDate || "", true),
      },
      {
        label: "바이인금액",
        name: "[바이인금액]",
        value: `${exchangeSeed(data?.buyIn)} rabbit`,
      },
      {
        label: "현재레벨",
        name: "[현재레벨]",
        value: getCurrentLevel(),
      },
      {
        label: "스몰블라인드",
        name: "[스몰블라인드]",
        value: formatNumber(timeInfo.currentLevel?.smallBlind),
      },
      {
        label: "빅블라인드",
        name: "[빅블라인드]",
        value: formatNumber(timeInfo.currentLevel?.bigBlind),
      },
      {
        label: "엔티",
        name: "[엔티]",
        value: formatNumber(timeInfo.currentLevel?.ante),
      },
      {
        label: "현재플레이어",
        name: "[현재플레이어]",
        value: data.userCount,
      },
      { label: "듀레이션", name: "[듀레이션]", value: getDurations() },
      {
        label: "전체엔트리",
        name: "[전체엔트리]",
        value: (
          (data.entryUserCount || 0) +
          (data.reBuyInUserCount || 0) +
          (data.adOnUserCount || 0)
        ).toLocaleString(),
      },
      {
        label: "바이인칩",
        name: "[바이인칩]",
        value: formatNumber(data.buyInChip),
      },
      {
        label: "리엔트리칩",
        name: "[리엔트리칩]",
        value: formatNumber(data.reBuyInChip),
      },
      {
        label: "레지마감레벨",
        name: "[레지마감레벨]",
        value: data.registerDeadlineLevel,
      },
      {
        label: "레지마감시간",
        name: "[레지마감시간]",
        value: registerDeadlineTime(),
      },
    ];
  }, [
    data,
    timeInfo,
    registerDeadlineTime,
    getDurations,
    formatNumber,
    getCurrentLevel,
  ]);

  const warning = useMemo(() => {
    if (!presetContent) return "텍스트가 비어있습니다.";

    const matches = presetContent.match(/\[.*?\]/g) || [];
    const validVariableNames = presetVariables.map((v) => v.name);

    for (const match of matches) {
      if (!validVariableNames.includes(match)) {
        return "등록되지 않은 변수가 사용되었습니다.";
      }
    }

    let tempContent = presetContent;
    validVariableNames.forEach((name) => {
      tempContent = tempContent.replaceAll(name, "");
    });

    if (tempContent.includes("[") || tempContent.includes("]")) {
      return "대괄호 문자([])는 텍스트로 사용 불가능합니다.";
    }

    return "";
  }, [presetContent, presetVariables]);

  // 프리셋 미리보기
  const previewContent = () => {
    let content = presetContent;
    presetVariables.forEach((variable) => {
      content = content.replaceAll(variable.name, String(variable.value));
    });
    return content;
  };

  const CopyText = async () => {
    await navigator.clipboard.writeText(previewContent());
    alert(`텍스트가 복사되었습니다.`);
  };

  const registerPreset = async () => {
    if (!pub) return;
    if (!presetName) {
      alert("프리셋 이름을 입력해주세요.");
      return;
    }
    if (warning) {
      alert(warning);
      return;
    }

    try {
      await registerTournamentStatusPreset({
        pubId: pub.id,
        presetName: presetName,
        preset: presetContent,
      });
      alert("프리셋이 생성되었습니다.");
      setIsDialogOpen(false);
      getData();
    } catch (error) {
      console.error(error);
    }
  };

  const updatePreset = async () => {
    if (!presetName || !pub || selectedPreset.id === -1) return;
    if (warning) return;
    try {
      await updateTournamentStatusPreset(selectedPreset.id, {
        pubId: pub.id,
        presetName: presetName,
        preset: presetContent,
      });
      alert("프리셋이 업데이트되었습니다.");
      setIsDialogOpen(false);
      getData();
    } catch (error) {
      console.error(error);
    }
  };

  const deletePreset = async () => {
    if (selectedPreset.id === -1) return;
    try {
      await removeTournamentStatusPreset(selectedPreset.id);
      alert("프리셋이 삭제되었습니다.");
      setIsDialogOpen(false);
      getData();
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <S.TimerControllerStatusWrapper onClick={() => setIsDropBoxOpen(false)}>
      {isDialogOpen && (
        <Overlay>
          <InputDialog
            title="프리셋 생성"
            content={presetName}
            setContent={setPresetName}
            setIsOpen={setIsDialogOpen}
            confirm={registerPreset}
          />
        </Overlay>
      )}
      <S.TimerControllerStatusHeader>
        <S.TimerControllerStatusHeaderTitle>
          <div className="name">프리셋</div>
          <div
            className="input"
            onClick={(e) => {
              e.stopPropagation();
              setIsDropBoxOpen(!isDropBoxOpen);
            }}
          >
            <input
              type="text"
              value={presetName}
              onChange={(e) => setPresetName(e.target.value)}
              placeholder="프리셋 이름을 입력해주세요"
            />
            <img src="/image-web/tournament/drop-down.svg" alt="드롭다운" />
            {isDropBoxOpen && (
              <S.TimerControllerStatusDropBox>
                {list.map((item, index) => (
                  <div
                    className="item"
                    key={index}
                    onClick={() => setSelectedPreset(item)}
                  >
                    {item.presetName}
                  </div>
                ))}
              </S.TimerControllerStatusDropBox>
            )}
          </div>
          {selectedPreset.id !== -1 && (
            <img
              src="/image-web/controller/trash-can.svg"
              alt="삭제"
              onClick={deletePreset}
            />
          )}
        </S.TimerControllerStatusHeaderTitle>
        <S.TimerControllerStatusHeaderButton>
          <div onClick={() => setIsDialogOpen(true)}>
            <img src="/image-web/controller/preset-plus.svg" alt="새 프리셋" />
            프리셋 생성
          </div>
          {selectedPreset.id !== -1 && (
            <div className="save" onClick={updatePreset}>
              <img src="/image-web/controller/preset-save.svg" alt="저장" />
              업데이트
            </div>
          )}
        </S.TimerControllerStatusHeaderButton>
      </S.TimerControllerStatusHeader>
      <S.TimerControllerStatusContent>
        <S.TimerControllerInputWrapper>
          <S.TimerControllerItemWrapper>
            <div className="title">사용 가능 변수</div>
            <S.TimerControllerVariableWrapper>
              {presetVariables.map((variable, index) => (
                <div
                  className="item"
                  key={index}
                  onClick={() => handleVariableClick(variable.name)}
                >
                  {variable.label}
                </div>
              ))}
            </S.TimerControllerVariableWrapper>
          </S.TimerControllerItemWrapper>
          <S.TimerControllerItemWrapper>
            <div className="title">
              진행현황 작성
              <span onClick={() => setPresetContent("")}>
                <img src="/image-web/controller/preset-reset.svg" alt="리셋" />
                초기화
              </span>
            </div>
            <textarea
              ref={textareaRef}
              value={presetContent}
              onChange={(e) => setPresetContent(e.target.value)}
              placeholder="텍스트를 입력하고 변수를 삽입하세요. "
            ></textarea>
            <S.TimerControllerNote $warning={!!warning}>
              <img
                src={`/image-web/controller/${warning ? "warning" : "can-save"}.svg`}
                alt="경고"
              />
              {warning || "저장이 가능합니다"}
            </S.TimerControllerNote>
          </S.TimerControllerItemWrapper>
        </S.TimerControllerInputWrapper>
        <S.TimerControllerPreviewWrapper>
          <S.TimerControllerItemWrapper>
            <div className="title">미리보기</div>
            <textarea value={previewContent()} readOnly></textarea>
            <div className="button" onClick={() => CopyText()}>
              <img src="/image-web/controller/copy.svg" alt="복사" />
              복사
            </div>
          </S.TimerControllerItemWrapper>
        </S.TimerControllerPreviewWrapper>
      </S.TimerControllerStatusContent>
    </S.TimerControllerStatusWrapper>
  );
};

export default TimerControllerStatus;
